import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  Car, 
  ArrowLeft,
  User,
  Phone,
  Mail,
  FileText,
  CheckCircle,
  Loader2,
  Shield,
  Clock,
  DollarSign
} from 'lucide-react';

const DriverSignup = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [driverId, setDriverId] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    contact_number: '',
    email: '',
    licence_number: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.contact_number || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API}/drivers/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        throw new Error('Failed to submit application');
      }

      const data = await response.json();
      setDriverId(data.driver_id);
      setSubmitted(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    { icon: <Clock className="w-5 h-5" />, title: 'Flexible Hours', description: 'Work when you want, as much as you want' },
    { icon: <DollarSign className="w-5 h-5" />, title: 'Competitive Earnings', description: 'Keep more of what you earn with fair commission rates' },
    { icon: <Shield className="w-5 h-5" />, title: 'Full Support', description: 'Dedicated support team to help you succeed' }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-indigo-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-3xl p-8 text-center space-y-6 shadow-xl border border-slate-200" data-testid="submission-success">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-medium text-slate-900">Application Submitted</h1>
          <p className="text-slate-600">
            Thank you for your interest in driving with NeoRide! Your application has been received.
          </p>
          <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
            <p className="text-sm text-slate-500">Reference Number</p>
            <p className="text-lg font-mono text-slate-900">{driverId}</p>
          </div>
          <p className="text-sm text-slate-500">
            A member of our team will be in touch with you soon to discuss the next steps.
          </p>
          <Button 
            onClick={() => navigate('/')}
            className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
            data-testid="back-home-btn"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/')}
              className="text-slate-500 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-violet-600 to-indigo-600 rounded-lg flex items-center justify-center">
                <Car className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-semibold text-slate-900">NeoRide</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 bg-gradient-to-br from-violet-600 to-indigo-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        <div className="relative z-10 max-w-7xl mx-auto text-center py-16">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-violet-200 mb-4">Join Our Team</p>
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tighter text-white mb-4">
            Drive With NeoRide
          </h1>
          <p className="text-lg text-violet-100 max-w-2xl mx-auto">
            Join our team of professional private hire drivers and start earning on your own terms.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 px-4 -mt-8 relative z-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 space-y-3 shadow-lg border border-slate-200" data-testid={`benefit-${index}`}>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center text-violet-600">
                  {benefit.icon}
                </div>
                <h3 className="text-slate-900 font-medium">{benefit.title}</h3>
                <p className="text-sm text-slate-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 px-4 pb-24">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-3xl p-8 shadow-xl border border-slate-200" data-testid="driver-signup-form">
            <h2 className="text-2xl font-medium text-slate-900 mb-6">Apply Now</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="full_name" className="text-sm font-medium text-slate-700">
                  Full Name *
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400"
                    required
                    data-testid="input-full-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_number" className="text-sm font-medium text-slate-700">
                  Contact Number *
                </Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="contact_number"
                    name="contact_number"
                    type="tel"
                    value={formData.contact_number}
                    onChange={handleChange}
                    placeholder="Enter your phone number"
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400"
                    required
                    data-testid="input-contact-number"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                  Email Address *
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter your email"
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="licence_number" className="text-sm font-medium text-slate-700">
                  Wolverhampton Private Hire Licence Number
                </Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="licence_number"
                    name="licence_number"
                    value={formData.licence_number}
                    onChange={handleChange}
                    placeholder="Enter licence number (if you have one)"
                    className="pl-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400"
                    data-testid="input-licence"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-sm font-medium text-slate-700">
                  Additional Information
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your experience..."
                  className="bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 min-h-[100px]"
                  data-testid="input-message"
                />
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700 py-6 rounded-xl ai-glow"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Application'
                )}
              </Button>
            </form>

            <p className="text-xs text-slate-400 text-center mt-6">
              By submitting this form, you agree to be contacted by NeoRide regarding your application.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DriverSignup;
